import "./BidListingPageNew.css";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LuMoveLeft } from "react-icons/lu";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { API_BASE_URL } from "../utils";
import PackageNewCard from "./comp/PackageNewCard";
import BidFilterComp from "./comp/BidFilterComp";
import BidFilterCarousel from "./comp/BidFilterCarousel";
import Loginpopup from "../Navbar/Loginmodal";
import BidNewCard from "./comp/BidNewCard";
import BidNewCardSkeleton from "./comp/BidNewCardSkeleton";
import { useGeoLocation } from "../context/LocationContext";
import FilterBar from "./FilterBar";
import Loader from "../Loader/Loader";

const filters = [
  {
    label: "Live Music",
    type: "filter",
    icon: "IoIosMusicalNotes",
  },
  {
    label: "Distance",
    type: "dropdown",
    icon: "IoIosArrowDown",
    options: [
      "Under 5 Km",
      "Under 10 Km",
      "Under 20 Km",
      "Under 30 Km",
      "Under 50 Km",
    ],
  },
  {
    label: "Review",
    type: "dropdown",
    icon: "IoIosArrowDown",
    options: ["1 stars", "2 stars", "3 stars", "4 stars", "5 stars"],
  },
];

export default function BidListingPageNew() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { latitude, longitude } = useGeoLocation();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const sourcePage = searchParams.get("source");
  const userdata = JSON.parse(localStorage.getItem("userData"));
  const [filteredBids, setFilteredBids] = useState([]);
  const [showLoginPopup, setShowLoginPopup] = useState(false);

  // Single source of truth for filters
  const [filtersAndSort, setFiltersAndSort] = useState({
    selectedDropdowns: {},
    selectedPills: [],
  });

  const [packageData, setPackageData] = useState([]);
  const [selectedLead, setSelectedLead] = useState(id || "");

  const fetchPackageBids = useCallback(async (latitude, longitude, userdata) => {
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("latitude", latitude);
      formData.append("longitude", longitude);
      formData.append("user_id", userdata.id);

      const { data } = await axios.post(
        `${API_BASE_URL}/master/APIs/ClubPackage/getGroupBookingUserBids`,
        formData
      );
      const leads = data.status ? data.data || [] : [];
      setPackageData(leads);

      // ✅ Auto-select first lead if no lead selected in URL
      if (leads.length > 0 && !id) {
        const firstLeadId = leads[0].id;
        setSelectedLead(firstLeadId);
        navigate(`/bid-list/${firstLeadId}?source=package`, { replace: true });
      }
    } catch {
      setPackageData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPackageBids(latitude, longitude, userdata);
  }, []);

  // Fetch Bids
  const fetchBids = async ({ queryKey }) => {
    const [, leadId, userId, latitude, longitude] = queryKey;
    const formData = new FormData();
    formData.append("latitude", latitude);
    formData.append("longitude", longitude);
    formData.append("user_id", userId);
    formData.append("lead_id", leadId);

    const response = await axios.post(
      `${API_BASE_URL}/master/APIs/ClubPackage/getGroupLeadDetails`,
      formData
    );

    if (!response.data.status) {
      throw new Error(response.data.msg || "Failed to fetch bids");
    }

    return response.data.data;
  };

  const {
    data: dataInfo = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["bids", selectedLead, userdata?.id, latitude, longitude],
    queryFn: fetchBids,
    enabled: !!selectedLead && !!latitude && !!longitude && !!userdata?.id,
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 30,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: 2,
  });

  const data = dataInfo.bids || [];

  // ✅ Load filters from URL ONCE on mount
  useEffect(() => {
    const review = searchParams.get("review");
    const distance = searchParams.get("distance");
    const sort = searchParams.get("sort");
    const pillsParam = searchParams.get("filter"); // ✅ keep consistent with below useEffect

    const pills = pillsParam ? pillsParam.split(",") : [];
    const hasFilters = review || distance || sort || pills.length > 0;

    if (hasFilters) {
      setFiltersAndSort({
        selectedDropdowns: {
          ...(review && { Review: [review] }),
          ...(distance && { Distance: distance }),
          ...(sort && { "Sort By": sort }),
        },
        selectedPills: pills,
      });
    }
  }, []);

  // ✅ Update URL when filters change (preserve source param)
  useEffect(() => {
    const { selectedDropdowns, selectedPills } = filtersAndSort;

    // ✅ Always read the *latest* params directly from window.location
    const currentParams = new URLSearchParams(window.location.search);

    // --- Handle dropdown filters
    Object.entries(selectedDropdowns).forEach(([key, value]) => {
      const paramKey = key.toLowerCase();

      if (value && value.length > 0) {
        const paramValue = Array.isArray(value) ? value[0] : value;
        currentParams.set(paramKey, paramValue);
      } else {
        currentParams.delete(paramKey); // ✅ remove when empty
      }
    });

    // --- Handle pills (Live Music etc.)
    if (selectedPills?.length > 0) {
      currentParams.set("filter", selectedPills.join(","));
    } else {
      currentParams.delete("filter"); // ✅ remove when none
    }

    // --- Preserve `source=package`
    const existingSource = searchParams.get("source");
    if (existingSource) {
      currentParams.set("source", existingSource);
    }

    // --- Remove unnecessary params (if filters are empty)
    if (
      Object.keys(selectedDropdowns).length === 0 &&
      selectedPills.length === 0
    ) {
      // Keep only source param
      const cleanParams = new URLSearchParams();
      if (existingSource) cleanParams.set("source", existingSource);
      setSearchParams(cleanParams, { replace: true });
      return;
    }

    // --- Compare and update if needed
    const newSearch = currentParams.toString();
    const currentSearch = window.location.search.replace("?", "");

    if (newSearch !== currentSearch) {
      setSearchParams(currentParams, { replace: true });
    }
  }, [filtersAndSort]);



  // ✅ Optimized filter logic
  const filteredData = useMemo(() => {
    const { selectedDropdowns, selectedPills } = filtersAndSort;

    const hasReview = selectedDropdowns?.Review?.length > 0;
    const hasDistance = !!selectedDropdowns?.Distance;
    const hasLiveMusic = selectedPills?.includes("Live Music");
    const hasSort = !!selectedDropdowns?.["Sort By"];

    const hasAnyFilter = hasReview || hasDistance || hasLiveMusic || hasSort;
    if (!hasAnyFilter) return [];

    let filtered = data.filter((item) => {
      // 🔹 Review Filter
      if (hasReview) {
        const selectedReview = parseInt(selectedDropdowns.Review[0]);
        const itemRating = parseFloat(item.totalAverageRating);
        const lower = selectedReview - 0.4;
        const upper = selectedReview + 0.5;
        if (isNaN(itemRating) || itemRating < lower || itemRating >= upper) {
          return false;
        }
      }

      // 🔹 Distance Filter
      if (hasDistance) {
        const distanceValue = Array.isArray(selectedDropdowns.Distance)
          ? selectedDropdowns.Distance[0]
          : selectedDropdowns.Distance;
        const selectedDistance = parseInt(distanceValue.match(/\d+/)?.[0] || "0", 10);
        const itemDistance = parseFloat(item.distance);
        if (isNaN(itemDistance) || itemDistance > selectedDistance) {
          return false;
        }
      }

      // 🔹 Live Music
      if (hasLiveMusic) {
        if (item.category_name?.toLowerCase() !== "live music") {
          return false;
        }
      }

      return true;
    });

    // 🔹 Sort
    if (hasSort) {
      const sortType = selectedDropdowns["Sort By"];
      filtered = [...filtered].sort((a, b) => {
        if (sortType === "A to Z") return a.title.localeCompare(b.title);
        if (sortType === "Z to A") return b.title.localeCompare(a.title);
        return 0;
      });
    }

    return filtered;
  }, [data, filtersAndSort]);

  // ✅ Sync filtered data to render arrays
  useEffect(() => {
    setFilteredBids(filteredData);
  }, [filteredData]);

  const handleLeadSelect = (leadId) => {
    setSelectedLead(leadId);
    navigate(`/bid-list/${leadId}?source=package`);
  };

  const filterWiseCount = useMemo(() => {
    const counts = {};
    const dataToFilter = data || [];

    // Distance counts
    const distanceOptions = ["Under 5 Km", "Under 10 Km", "Under 20 Km", "Under 30 Km", "Under 50 Km"];
    distanceOptions.forEach((opt) => {
      const selectedDistance = parseInt(opt.match(/\d+/)?.[0] || "0", 10);
      const matched = dataToFilter.filter(
        (item) => parseFloat(item.distance) <= selectedDistance
      );
      counts[opt] = matched.length;
    });

    // Review counts
    const reviewOptions = ["1 stars", "2 stars", "3 stars", "4 stars", "5 stars"];
    reviewOptions.forEach((opt) => {
      const selectedReview = parseInt(opt);
      const matched = dataToFilter.filter((item) => {
        const rating = parseFloat(item.totalAverageRating);
        return !isNaN(rating) && rating >= selectedReview - 0.4 && rating < selectedReview + 0.5;
      });
      counts[opt] = matched.length;
    });

    // Live Music count
    counts["Live Music"] = dataToFilter.filter(
      (item) => item.category_name?.toLowerCase() === "live music"
    ).length;

    return counts;
  }, [data]);


  const handleLogin = (ud) => {
    if (ud) {
      localStorage.setItem("userData", JSON.stringify(ud));
      window.location.reload();
      setShowLoginPopup(false);
    }
  };

  useEffect(() => {
    const userData = localStorage.getItem("userData");
    if (!userData) setShowLoginPopup(true);
  }, []);

  if (isError) {
    return (
      <div className="bid-listing-page-new-error">
        <div className="bid-listing-page-new-header">
          <h1>
            <LuMoveLeft onClick={() => navigate(-1)} style={{ cursor: "pointer" }} />{" "}
            Bid listing
          </h1>
        </div>
        <div className="bid-listing-page-new-container-error">
          <h5>{error.message}</h5>
          <button
            className="bid-listing-page-new-container-error-button"
            onClick={() => navigate("/")}>
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {loading && <Loader />}
      {showLoginPopup && <Loginpopup clbk={handleLogin} isbackendUrl={true} />}

      <div className="bid-listing-page-new">
        <div className="bid-listing-page-new-header">
          <h1>
            <span onClick={() => navigate(-1)} style={{ cursor: "pointer" }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="15" viewBox="0 0 18 15" fill="none">
                <path
                  d="M17 7.5H1M1 7.5L7 1.5M1 7.5L7 13.5"
                  stroke="black"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>{" "}
            My Bids
          </h1>

          <div className="bid-listing-page-new-header-date-section">
            {packageData?.map((item, index) => (
              <div
                className={`bid-listing-page-new-header-date ${selectedLead === item.id ? "active" : ""}`}
                key={index}
                onClick={() => handleLeadSelect(item.id)}>
                {item.party_date}
              </div>
            ))}
          </div>
        </div>

        <div className="bid-deatil-info-section">
          <div className="bid-deatils-info-section-boxes">
            <div className="party-detils-set">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="20"
                viewBox="0 0 18 20"
                fill="none">
                <path
                  d="M16 2H15V0H13V2H5V0H3V2H2C0.89 2 0.00999999 2.9 0.00999999 4L0 18C0 19.1 0.89 20 2 20H16C17.1 20 18 19.1 18 18V4C18 2.9 17.1 2 16 2ZM16 18H2V8H16V18ZM16 6H2V4H16V6ZM6 12H4V10H6V12ZM10 12H8V10H10V12ZM14 12H12V10H14V12ZM6 16H4V14H6V16ZM10 16H8V14H10V16ZM14 16H12V14H14V16Z"
                  fill="white"
                />
              </svg>
              Party Date :
            </div>
            <p>{dataInfo?.party_date || "--|--|---"}</p>
          </div>
          <div className="bid-deatils-info-section-boxes">
            <div className="party-detils-set">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none">
                <path
                  d="M15.666 1.5H9.66602V3.5H15.666V1.5ZM11.666 14.5H13.666V8.5H11.666V14.5ZM19.696 7.89L21.116 6.47C20.686 5.96 20.216 5.48 19.706 5.06L18.286 6.48C16.736 5.24 14.786 4.5 12.666 4.5C7.69602 4.5 3.66602 8.53 3.66602 13.5C3.66602 18.47 7.68602 22.5 12.666 22.5C17.646 22.5 21.666 18.47 21.666 13.5C21.666 11.38 20.926 9.43 19.696 7.89ZM12.666 20.5C8.79602 20.5 5.66602 17.37 5.66602 13.5C5.66602 9.63 8.79602 6.5 12.666 6.5C16.536 6.5 19.666 9.63 19.666 13.5C19.666 17.37 16.536 20.5 12.666 20.5Z"
                  fill="white"
                />
              </svg>
              Party Time slot :
            </div>
            <p>{dataInfo?.party_time || "--:--"} Onwards </p>
          </div>
          <div className="bid-deatils-info-section-boxes">
            <div className="party-detils-set">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25"
                height="24"
                viewBox="0 0 25 24"
                fill="none">
                <path
                  d="M9.93598 5.609H3.47698M18.612 5.0235L12.164 6.551M3.34598 3.045C3.33831 3.3116 3.33431 3.57829 3.33398 3.845C3.33398 9.297 4.84298 13.7165 6.70398 13.7165C8.56498 13.7165 10.074 9.2965 10.074 3.8455C10.0733 3.57583 10.0693 3.30917 10.062 3.0455M11.5335 4.054C11.5868 4.31267 11.6447 4.573 11.707 4.835C12.968 10.139 15.4585 14.089 17.269 13.6585C19.0795 13.228 19.525 8.5795 18.264 3.276C18.2019 3.01644 18.1362 2.75775 18.067 2.5M6.70398 13.997V20.456M3.61498 20.456H9.79298M17.334 13.9315L18.829 20.2155M15.8235 20.93L21.834 19.5005"
                  stroke="white"
                  stroke-width="1.2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
              Package Type :
            </div>
            <p>{dataInfo?.title || "--"} </p>
          </div>
          <div className="bid-deatils-info-section-boxes">
            <div className="party-detils-set">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="15"
                viewBox="0 0 18 15"
                fill="none">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M6.1875 5.75C6.63505 5.75 7.06428 5.57221 7.38074 5.25574C7.69721 4.93928 7.875 4.51005 7.875 4.0625C7.875 3.61495 7.69721 3.18572 7.38074 2.86926C7.06428 2.55279 6.63505 2.375 6.1875 2.375C5.73995 2.375 5.31072 2.55279 4.99426 2.86926C4.67779 3.18572 4.5 3.61495 4.5 4.0625C4.5 4.51005 4.67779 4.93928 4.99426 5.25574C5.31072 5.57221 5.73995 5.75 6.1875 5.75ZM6.1875 7.4375C7.08261 7.4375 7.94105 7.08192 8.57399 6.44899C9.20692 5.81605 9.5625 4.95761 9.5625 4.0625C9.5625 3.16739 9.20692 2.30895 8.57399 1.67601C7.94105 1.04308 7.08261 0.6875 6.1875 0.6875C5.29239 0.6875 4.43395 1.04308 3.80101 1.67601C3.16808 2.30895 2.8125 3.16739 2.8125 4.0625C2.8125 4.95761 3.16808 5.81605 3.80101 6.44899C4.43395 7.08192 5.29239 7.4375 6.1875 7.4375ZM2.77987 10.6842C1.90575 11.2917 1.6875 11.8801 1.6875 12.1535C1.6875 12.6553 2.09475 13.0625 2.5965 13.0625H9.7785C10.0194 13.0619 10.2503 12.9659 10.4206 12.7956C10.5909 12.6253 10.6869 12.3944 10.6875 12.1535C10.6875 11.879 10.4693 11.2906 9.59513 10.6842C8.766 10.1094 7.55887 9.6875 6.1875 9.6875C4.81613 9.6875 3.609 10.1094 2.77987 10.6842ZM0 12.1535C0 10.0768 2.78437 8 6.1875 8C7.515 8 8.75025 8.31613 9.76163 8.82575C10.7301 8.27825 11.825 7.99358 12.9375 8C15.7219 8 18 9.73025 18 11.4616C18 12.0354 17.7721 12.5857 17.3664 12.9914C16.9607 13.3971 16.4104 13.625 15.8366 13.625H11.9182C11.4502 14.3045 10.6661 14.75 9.7785 14.75H2.5965C1.90805 14.7494 1.24796 14.4757 0.761155 13.9888C0.274346 13.502 0.000595805 12.842 0 12.1535ZM12.3649 11.9375H15.8366C16.0999 11.9375 16.3125 11.7237 16.3125 11.4616C16.3125 11.3457 16.2045 10.9306 15.5385 10.4604C14.9153 10.0183 13.9916 9.6875 12.9375 9.6875C12.3559 9.6875 11.8136 9.78875 11.34 9.953C11.9317 10.5538 12.3008 11.2423 12.3649 11.9375ZM14.0625 4.625C14.0625 4.92337 13.944 5.20952 13.733 5.42049C13.522 5.63147 13.2359 5.75 12.9375 5.75C12.6391 5.75 12.353 5.63147 12.142 5.42049C11.931 5.20952 11.8125 4.92337 11.8125 4.625C11.8125 4.32663 11.931 4.04048 12.142 3.82951C12.353 3.61853 12.6391 3.5 12.9375 3.5C13.2359 3.5 13.522 3.61853 13.733 3.82951C13.944 4.04048 14.0625 4.32663 14.0625 4.625ZM15.75 4.625C15.75 5.37092 15.4537 6.08629 14.9262 6.61374C14.3988 7.14118 13.6834 7.4375 12.9375 7.4375C12.1916 7.4375 11.4762 7.14118 10.9488 6.61374C10.4213 6.08629 10.125 5.37092 10.125 4.625C10.125 3.87908 10.4213 3.16371 10.9488 2.63626C11.4762 2.10882 12.1916 1.8125 12.9375 1.8125C13.6834 1.8125 14.3988 2.10882 14.9262 2.63626C15.4537 3.16371 15.75 3.87908 15.75 4.625Z"
                  fill="white"
                />
              </svg>
              Pax :
            </div>
            <p>{(Number(dataInfo?.male) + Number(dataInfo?.female)) > 0 ? (Number(dataInfo?.male) + Number(dataInfo?.female)) : "--"} Guests</p>
          </div>
        </div>

        <div className="bid-listing-page-new-container">
          <div className="bid-listing-page-filter-section">
            <FilterBar
              filterList={filters}
              filtersAndSort={filtersAndSort}
              setFiltersAndSort={setFiltersAndSort}
              filterWiseCount={filterWiseCount}
            />
          </div>


          {filteredBids?.length > 0 && (
            <div className="bid-listing-page-new-header-crousale">
              <BidFilterCarousel data={filteredBids} isBid={true} />
            </div>
          )}

          <div className="bid-listing-page-new-content-container">
            {isLoading
              ? Array.from({ length: 4 }).map((_, idx) => <BidNewCardSkeleton key={idx} />)
              : (filteredBids.length > 0 ? filteredBids : data)?.map((item, index) => (
                <BidNewCard
                  key={index}
                  data={item}
                  isBid={true}
                  isFiltered={filteredBids.some((f) => f?.id === item.id)}
                />
              ))}
          </div>
        </div>
      </div>
    </>
  );
}
